import { isPolygon } from '@/modules/Editor/annotationTypes/polygon'
import type { ViewEvent } from '@/modules/Editor/eventBus'
import {
  ActionManagerEvents,
  AnnotationManagerEvents,
  CameraEvents,
} from '@/modules/Editor/eventBus'
import type { Annotation } from '@/modules/Editor/models/annotation/Annotation'
import type { View } from '@/modules/Editor/views/view'

import { drawMaskPreview, drawPolygonPreview } from './drawSlicePreview'
import { planMaskSlice, type MaskSlicePlan } from './planMaskSlice'
import { planPolygonSlice, type PolygonSlicePlan } from './planPolygonSlice'
import { sliceData } from './sliceTarget'
import { applyMaskSlice, applyPolygonSlice, readMaskCrop } from './sliceActions'
import type { SliceSession } from './SliceController'

export const canSlice = (annotation: Annotation | undefined): annotation is Annotation =>
  annotation?.type === 'polygon' || annotation?.type === 'mask'

const isSelectedTarget = (view: View, target: Annotation): boolean =>
  view.annotationManager.selectedAnnotation?.id === target.id

// Any update to the target (geometry, class, metadata, a teammate's change) replaces its data object.
const isUnchangedTarget = (view: View, target: Annotation): boolean =>
  isSelectedTarget(view, target) &&
  view.annotationManager.getAnnotation(target.id)?.data === target.data

// Selection changes, deleting the target, and any undo or redo invalidate a slice in progress.
const watchTarget =
  (view: View, target: Annotation) =>
  (onInvalid: () => void, onViewChange: () => void = (): void => undefined): (() => void) => {
    const onSelect = (): void => {
      if (
        !isSelectedTarget(view, target) ||
        view.annotationManager.selectedAnnotations.length !== 1
      ) {
        onInvalid()
      }
    }
    const onDelete = (_: ViewEvent, annotationId: string): void => {
      if (annotationId === target.id) {
        onInvalid()
      }
    }
    AnnotationManagerEvents.annotationsSelect.on(onSelect)
    AnnotationManagerEvents.annotationDelete.on(onDelete)
    ActionManagerEvents.doneActionsChanged.on(onInvalid)
    ActionManagerEvents.undoneActionsChanged.on(onInvalid)
    CameraEvents.scaleChanged.on(onViewChange)
    CameraEvents.offsetChanged.on(onViewChange)
    return () => {
      AnnotationManagerEvents.annotationsSelect.off(onSelect)
      AnnotationManagerEvents.annotationDelete.off(onDelete)
      ActionManagerEvents.doneActionsChanged.off(onInvalid)
      ActionManagerEvents.undoneActionsChanged.off(onInvalid)
      CameraEvents.scaleChanged.off(onViewChange)
      CameraEvents.offsetChanged.off(onViewChange)
    }
  }

export const createSliceSession = (
  view: View,
  target: Annotation,
): SliceSession<PolygonSlicePlan> | SliceSession<MaskSlicePlan> | null => {
  const shape = sliceData(view, target)
  if (target.type === 'polygon' && shape && isPolygon(shape)) {
    const data = shape
    const session: SliceSession<PolygonSlicePlan> = {
      plan: (stroke) => planPolygonSlice(data, stroke),
      apply: (plan, isCurrent) => applyPolygonSlice(view, target, plan, isCurrent),
      draw: (preview) => drawPolygonPreview(view, preview),
      isStillValid: () => isUnchangedTarget(view, target),
      watch: watchTarget(view, target),
    }
    return session
  }
  if (target.type === 'mask') {
    const crop = readMaskCrop(view, target)
    if (!crop) {
      return null
    }
    const session: SliceSession<MaskSlicePlan> = {
      plan: (stroke) => planMaskSlice(crop, stroke),
      apply: (plan, isCurrent) => applyMaskSlice(view, target, crop, plan, isCurrent),
      draw: (preview) => drawMaskPreview(view, crop, preview),
      // pixel changes are caught by the undo/redo watch and re-checked before applying
      isStillValid: () => isUnchangedTarget(view, target),
      watch: watchTarget(view, target),
    }
    return session
  }
  return null
}
